// EVM Wallet specific view components for Withdraw
import React, { useMemo } from 'react'
import {
  formatAmount,
  validateAndFormatInput,
} from '../../../Deposit/src/utils'
import { isEthChain } from '../constants'
import BigNumber from 'bignumber.js'
// i18n
import { useTranslation } from '@/hooks'
// components
import { Tooltip } from '@/components/Tooltip'
import WithdrawSpeed from '../Speed'
// constants
import { SPEED } from '@/components/Withdraw/constants'
// assets
import MPCDepositWarnIcon from '@/icons/mpc-deposit-warn-icon.png'

interface WithdrawWalletViewProps {
  withdrawAmount: string
  curSelectToken: string
  tokenInfo: any
  curSelectTokenData: any
  currentActiveChain: any // 当前活跃链信息
  currentEquity: number
  amount: string
  decimals: number
  // 余额相关props
  tokenBalance?: string // 当前选中代币的余额
  platformBalance?: string // 用户在平台上的当前余额
  // 手续费
  fee?: string // 手续费金额
  // MPC 钱包相关
  walletType?: 'evm' | 'mpc' // 钱包类型
  receiverAddress?: string // 接收地址
  // 链ID => 资金池余额 的映射
  poolBalanceMap?: Record<number, string>
  onWithdrawAmountChange: (amount: string) => void
  onReceiverAddressChange?: (address: string) => void // 接收地址变化回调
  onAmountChanged?: (amount: string) => void // 金额失去焦点时的回调
  // 是否展示“超过资金池余额”的提示文案
  showPoolBalanceInsufficientTip?: boolean
}

export const WithdrawWalletView: React.FC<WithdrawWalletViewProps> = ({
  withdrawAmount,
  curSelectToken,
  tokenInfo,
  currentActiveChain, // 当前活跃链信息
  platformBalance = '0', // 默认值为 '0'
  fee = '0.00', // 默认手续费为 '0.00'
  walletType = 'evm', // 默认为 EVM 钱包
  receiverAddress = '', // 默认为空字符串
  onWithdrawAmountChange,
  onReceiverAddressChange,
  onAmountChanged,
  showPoolBalanceInsufficientTip = false,
}) => {
  // i18n
  const { t } = useTranslation()

  // 计算实际接收金额（用户输入金额 - 手续费）
  const actualReceiveAmount = useMemo(() => {
    if (!isEthChain(currentActiveChain?.chainId)) {
      const result = new BigNumber(withdrawAmount || '0')
        .minus(fee || '0')
        .decimalPlaces(2, BigNumber.ROUND_DOWN)

      // 如果结果为负数，返回 0.00
      return result.isNegative() ? '0.00' : result.toString()
    }
    return withdrawAmount || '0'
  }, [withdrawAmount, fee, currentActiveChain?.chainId])

  // 获取用户实际余额（不减去手续费）
  const actualBalance = useMemo(() => {
    return platformBalance || '0'
  }, [platformBalance])

  // Memoized fee rate percent display using BigNumber for precision
  const displayFeeRatePercent = useMemo(() => {
    const raw = (tokenInfo?.feeRate || currentActiveChain?.feeRate || 0) as
      | string
      | number
    try {
      return new BigNumber(raw || 0).times(100).toString()
    } catch {
      return '0'
    }
  }, [tokenInfo?.feeRate, currentActiveChain?.feeRate])

  // 处理输入变化的函数
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const maxBalance = actualBalance || '0' // 使用用户实际余额

    // 验证和格式化输入
    const validation = validateAndFormatInput(inputValue, maxBalance)

    if (validation.isValid) {
      // 如果输入有效，更新值
      onWithdrawAmountChange(validation.formattedValue)

      // 如果是自动调整到最大值，可以在这里添加用户提示（可选）
      if (validation.errorType === 'auto_adjusted_to_max') {
        // 可以在这里添加一个轻微的视觉反馈，比如短暂高亮
        // 或者显示一个提示消息，但为了简洁，我们暂时不添加
      }
    } else {
      // 如果输入无效，根据错误类型处理
      if (validation.errorType === 'exceed_balance') {
        // 这个分支现在应该不会被触发，因为我们改为自动调整
        return
      } else {
        // 其他错误（无效字符等），不更新输入值
        return
      }
    }
    onAmountChanged?.(validation.formattedValue)
  }

  // 设置最大金额（用户实际余额）
  const handleMaxClick = () => {
    onWithdrawAmountChange(actualBalance)

    onAmountChanged?.(actualBalance)
  }

  // 格式化余额显示 - 显示用户实际余额
  const formatBalanceDisplay = () => {
    const balance = parseFloat(platformBalance || '0')
    const inputAmount = parseFloat(withdrawAmount || '0')

    // 如果用户输入了金额，显示 "钱包余额 -> (钱包余额 - 输入金额)" 格式
    if (inputAmount > 0) {
      const remainingBalance = balance - inputAmount

      // 确保剩余余额不为负数
      const formattedRemaining = formatAmount(
        Math.max(0, remainingBalance).toString()
      )

      return (
        <span className="flex items-center gap-1">
          {formatAmount(platformBalance || '0')}
          <span className="text-sm font-edgex-harmony text-functional-green-1 leading-[18px] font-medium">
            →
          </span>
          {formattedRemaining} {curSelectToken}
        </span>
      )
    }

    // 否则只显示原始钱包余额
    return `${formatAmount(platformBalance || '0')} ${curSelectToken}`
  }

  return (
    <>
      {/* Amount Input Section */}
      <div className="bg-fill-input-primary rounded-lg px-3 py-3 my-4">
        {/* Amount Input 和 MAX Button 在同一行 - 高度 18px */}
        <div className="flex justify-between items-center h-[18px] mb-3">
          <input
            type="text"
            placeholder={t('deposit.amount')}
            value={withdrawAmount}
            onChange={handleInputChange}
            className="flex-1 bg-transparent text-text-primary text-sm font-medium placeholder-text-tertiary placeholder:font-normal focus:outline-none border-none p-0 h-full"
          />
          <button
            onClick={handleMaxClick}
            className="text-theme-normal text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity h-full flex items-center"
          >
            {t('deposit.max')}
          </button>
        </div>

        {/* Divider Line */}
        <div className="h-[1px] bg-line-divider-secondary mb-2"></div>

        {/* Available Balance */}
        <div className="flex justify-between items-center">
          <Tooltip content={t('withdraw.availableDesc')}>
            <span className="text-text-tertiary text-xs leading-[14px] border-b border-dashed border-line-divider-primary">
              {t('deposit.available')}
            </span>
          </Tooltip>
          <span className="text-text-primary text-xs leading-[14px]">
            {formatBalanceDisplay()}
          </span>
        </div>
      </div>

      {/* MPC 钱包的接收地址输入框 - 但不包括 ETH 相关链 (主网和测试网) */}
      {walletType === 'mpc' && !isEthChain(currentActiveChain?.chainId) && (
        <div className="bg-fill-input-primary rounded-lg my-4 h-9 flex items-center px-3">
          <input
            type="text"
            placeholder={t('withdraw.receiverAddress')}
            value={receiverAddress}
            onChange={e => onReceiverAddressChange?.(e.target.value)}
            className="flex-1 bg-transparent text-text-primary text-sm font-medium placeholder-text-tertiary placeholder:font-normal focus:outline-none border-none p-0 h-full"
          />
        </div>
      )}

      {/* 提现信息说明 */}
      <div className="bg-fill-page-secondary rounded-lg mb-4">
        {isEthChain(currentActiveChain?.chainId) ? (
          <>
            <p className="text-text-tertiary text-xs leading-[18px]">
              {t('withdraw.noticeDesc')}
            </p>
            <a
              className="block text-xs leading-[18px] mt-1 text-theme-normal"
              href="https://edgex-1.gitbook.io/edgeX-documentation/getting-started/deposits-and-withdrawals/withdrawals"
              rel="noreferrer"
              target="_blank"
            >
              {t('withdraw.commonWithdraw')}
            </a>
          </>
        ) : (
          <p className="text-text-tertiary text-xs leading-[18px]">
            {t('withdraw.crossChainTips')}
          </p>
        )}
      </div>

      {showPoolBalanceInsufficientTip && (
        <div className="flex mb-4">
          <div className="w-5">
            <img src={MPCDepositWarnIcon} className="w-4 h-4" alt="Warning" />
          </div>
          <div className="flex-1 text-xs text-functional-orange-1">
            {t('withdraw.poolBalanceInsufficientTip')}
          </div>
        </div>
      )}

      {/* 费用信息 */}
      <div className="py-4 border-t border-line-divider-secondary border-dashed">
        {/* Speed */}
        <div className="flex items-center justify-between h-[18px] leading-[18px]">
          <Tooltip
            content={
              <div>
                <div>{t('withdraw.speedNote')}</div>
                <div className="flex gap-1 items-center mt-1">
                  <WithdrawSpeed selectedLevel={SPEED.FAST} maxLevel={3} />
                  <span>{t('withdraw.speedNoteSpeed1')}</span>
                </div>
                <div className="flex gap-1 items-center mt-1">
                  <WithdrawSpeed selectedLevel={SPEED.Medium} maxLevel={3} />
                  <span>{t('withdraw.speedNoteSpeed2')}</span>
                </div>
                <div className="flex gap-1 items-center mt-1">
                  <WithdrawSpeed selectedLevel={SPEED.SLOW} maxLevel={3} />
                  <span>{t('withdraw.speedNoteSpeed3')}</span>
                </div>
              </div>
            }
          >
            <span className="text-text-tertiary text-sm leading-4 border-b border-dashed border-line-divider-primary">
              {t('withdraw.speed')}
            </span>
          </Tooltip>
          <div className="flex items-center">
            <WithdrawSpeed
              selectedLevel={
                isEthChain(currentActiveChain?.chainId)
                  ? SPEED.SLOW
                  : SPEED.FAST
              }
              maxLevel={3}
            />
          </div>
        </div>

        {/* Fee */}
        {!isEthChain(currentActiveChain?.chainId) && (
          <div className="flex items-center justify-between mt-2 h-[18px]">
            <Tooltip content={t('withdraw.feeCalculationTip')}>
              <span className="text-text-tertiary text-sm leading-4 border-b border-dashed border-line-divider-primary">
                {t('withdraw.fee')}/{t('withdraw.feeRate')}
              </span>
            </Tooltip>
            <span className="text-text-primary text-sm leading-4 font-medium">
              {fee} {curSelectToken} / {displayFeeRatePercent}%
            </span>
          </div>
        )}

        {/* Amount to receive */}
        <div className="flex items-center justify-between mt-2 h-[18px]">
          <span className="text-text-tertiary text-sm leading-4">
            {t(
              isEthChain(currentActiveChain?.chainId)
                ? 'withdraw.withdrawlAmount'
                : 'withdraw.amountToReceive'
            )}
          </span>
          <span className="text-text-primary text-sm leading-4 font-medium">
            {formatAmount(actualReceiveAmount)} {curSelectToken}
          </span>
        </div>
      </div>
    </>
  )
}
